import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, StatCard, Panel, Pill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import {
  Buildings, Baby, GraduationCap, Users, MagnifyingGlass, Megaphone,
  ShieldCheck, Sparkle, Path, Storefront,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

const STATUS_PILL = {
  open: { tone: "warning", label: "Open" },
  contacted: { tone: "info", label: "Contacted" },
  visited: { tone: "info", label: "Visited" },
  warm: { tone: "warning", label: "Warm" },
  hot: { tone: "danger", label: "Hot" },
  converted: { tone: "success", label: "Converted" },
  lost: { tone: "muted", label: "Lost" },
};

export default function FranchiseHqDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBroadcast, setShowBroadcast] = useState(false);

  const franchiseId = "fr_demo"; // single-franchise tenant for now

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/franchises/${franchiseId}/dashboard`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const branchChartData = useMemo(() => {
    if (!data?.per_branch) return [];
    return data.per_branch.map((b) => ({
      name: b.city || b.name,
      students: b.active_students,
      enquiries: b.open_enquiries,
      utilisation: b.utilisation_pct,
    }));
  }, [data]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-10 w-80 bg-paper-alt rounded-lg" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map((i) => <div key={i} className="h-28 bg-paper-alt rounded-2xl" />)}
          </div>
          <div className="h-72 bg-paper-alt rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-8 text-ink-soft">No data.</div>;

  return (
    <div data-testid="franchise-hq-dashboard">
      <PageHeader
        eyebrow="Franchise HQ Command Center"
        title={`${data.franchise?.display_name || "MangoCubs Franchise"}`}
        subtitle={`Operating across ${data.totals.branches} centre${data.totals.branches !== 1 ? "s" : ""} · ${data.totals.active_students} active students · ${data.totals.active_parents} families`}
        action={
          <>
            <Link to="/app/branches">
              <Button variant="outline" data-testid="manage-branches-btn">
                <Buildings size={16} /> Manage branches
              </Button>
            </Link>
            <Button
              variant="default"
              onClick={() => setShowBroadcast(true)}
              data-testid="broadcast-btn"
              className="bg-terracotta hover:bg-terracotta/90 text-white"
            >
              <Megaphone size={16} /> Broadcast to all
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Branches"
          value={data.totals.branches}
          icon={Buildings}
          accent="terracotta"
          testid="kpi-branches"
        />
        <StatCard
          label="Active students"
          value={data.totals.active_students}
          icon={Baby}
          accent="sage"
          testid="kpi-students"
        />
        <StatCard
          label="Active staff"
          value={data.totals.active_staff}
          icon={GraduationCap}
          accent="mustard"
          testid="kpi-staff"
        />
        <StatCard
          label="Open enquiries"
          value={data.totals.open_enquiries}
          icon={MagnifyingGlass}
          accent="lavender"
          testid="kpi-enquiries"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Active parents"
          value={data.totals.active_parents}
          icon={Users}
          accent="ink"
        />
        <StatCard
          label="Open support tickets"
          value={data.totals.open_support_tickets}
          icon={ShieldCheck}
          accent="terracotta"
          testid="kpi-tickets"
          hint={
            <Link to="/app/tech-support" className="underline text-xs">View all</Link>
          }
        />
        <StatCard
          label="Top performing branch"
          value={data.top_branch?.city || "—"}
          icon={Sparkle}
          accent="sage"
          hint={data.top_branch ? `${data.top_branch.active_students} students` : ""}
        />
        <StatCard
          label="Avg. utilisation"
          value={
            data.per_branch.length
              ? `${Math.round(
                  data.per_branch.reduce((s, b) => s + (b.utilisation_pct || 0), 0) /
                    data.per_branch.length,
                )}%`
              : "—"
          }
          icon={Path}
          accent="lavender"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Panel title="Branch performance" className="lg:col-span-2">
          <div className="h-72" data-testid="branch-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={branchChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE5DF" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="students" name="Students" fill="#E56B55" radius={[6, 6, 0, 0]} />
                <Bar dataKey="enquiries" name="Open Enquiries" fill="#F4B731" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Branch directory" className="lg:col-span-1">
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {data.per_branch.map((b) => (
              <Link
                key={b.branch_id}
                to={`/app/branches`}
                className="block p-3 rounded-xl bg-paper hover:bg-paper-alt border border-border transition"
                data-testid={`branch-row-${b.branch_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{b.city || b.name}</div>
                    <div className="text-xs text-ink-soft">
                      {b.active_students}/{b.capacity || "?"} students · {b.open_enquiries} open enquiries
                    </div>
                  </div>
                  <div
                    className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      b.utilisation_pct >= 80
                        ? "bg-sage-soft text-sage-deep"
                        : b.utilisation_pct >= 50
                        ? "bg-mustard-soft text-mustard-hover"
                        : "bg-paper-alt text-ink-soft"
                    }`}
                  >
                    {b.utilisation_pct}%
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <Panel
          title="Recent enquiries"
          right={
            <Link
              to="/app/enquiries"
              className="text-xs underline text-terracotta"
              data-testid="all-enquiries-link"
            >
              View all
            </Link>
          }
        >
          <div className="space-y-2">
            {data.recent_enquiries.length === 0 && (
              <div className="text-ink-soft text-sm py-6 text-center">
                No recent enquiries.
              </div>
            )}
            {data.recent_enquiries.map((e) => {
              const pill = STATUS_PILL[e.status] || { tone: "muted", label: e.status };
              return (
                <div
                  key={e.enquiry_id}
                  className="flex items-center justify-between p-2.5 rounded-lg bg-paper border border-border"
                  data-testid={`enquiry-${e.enquiry_id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {e.parent_name} · {e.child_name}{" "}
                      {e.age_group ? <span className="text-ink-soft">({e.age_group})</span> : null}
                    </div>
                    <div className="text-xs text-ink-soft truncate">
                      {e.source || "Direct"} · Form #{e.form_number || "—"}
                    </div>
                  </div>
                  <Pill tone={pill.tone}>{pill.label}</Pill>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel
          title="Recent admissions"
          right={
            <Link
              to="/app/admissions"
              className="text-xs underline text-terracotta"
              data-testid="all-admissions-link"
            >
              View all
            </Link>
          }
        >
          <div className="space-y-2">
            {data.recent_admissions.length === 0 && (
              <div className="text-ink-soft text-sm py-6 text-center">
                No recent admissions.
              </div>
            )}
            {data.recent_admissions.map((a) => (
              <div
                key={a.admission_id}
                className="flex items-center justify-between p-2.5 rounded-lg bg-paper border border-border"
                data-testid={`admission-${a.admission_id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {a.form_data?.child_name || "—"}{" "}
                    <span className="text-ink-soft text-xs">#{a.admission_number || a.admission_id}</span>
                  </div>
                  <div className="text-xs text-ink-soft truncate">
                    {a.form_data?.parent_name || ""}
                  </div>
                </div>
                <Pill tone={a.status === "approved" ? "success" : "warning"}>
                  {a.status}
                </Pill>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {showBroadcast && (
        <BroadcastModal
          franchiseId={franchiseId}
          onClose={() => setShowBroadcast(false)}
        />
      )}
    </div>
  );
}


function BroadcastModal({ franchiseId, onClose }) {
  const [title, setTitle] = useState("Franchise HQ Broadcast");
  const [message, setMessage] = useState("");
  const [audience, setAudience] = useState("all_staff");
  const [channel, setChannel] = useState("inapp");
  const [sending, setSending] = useState(false);

  async function send() {
    if (!message.trim()) {
      toast.error("Write a message first");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/franchises/${franchiseId}/broadcast`, {
        title,
        message,
        audience,
        channel,
      });
      toast.success(`Broadcast sent to ${r.data.sent} recipient${r.data.sent !== 1 ? "s" : ""}`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not send");
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="broadcast-modal"
    >
      <div
        className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-floating"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-lg font-heading font-bold mb-4">
          📢 Broadcast to all branches
        </div>

        <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
          Audience
        </label>
        <select
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          className="w-full mt-1 mb-3 p-2 rounded-lg border border-border bg-paper text-sm"
          data-testid="broadcast-audience"
        >
          <option value="all_staff">All staff (every centre)</option>
          <option value="all_parents">All parents (every centre)</option>
          <option value="all_users">Everyone in the franchise</option>
          <option value="branch_admins">Branch admins only</option>
          <option value="principals">Principals only</option>
          <option value="teachers">Teachers only</option>
        </select>

        <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
          Title
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full mt-1 mb-3 p-2 rounded-lg border border-border bg-paper text-sm"
          data-testid="broadcast-title"
        />

        <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
          Message
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          maxLength={2000}
          className="w-full mt-1 mb-3 p-2 rounded-lg border border-border bg-paper text-sm"
          data-testid="broadcast-message"
          placeholder="What's the news?"
        />

        <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
          Channel
        </label>
        <div className="flex gap-2 mt-1 mb-4">
          {["inapp", "email", "whatsapp", "all"].map((c) => (
            <button
              key={c}
              onClick={() => setChannel(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                channel === c
                  ? "bg-terracotta text-white border-terracotta"
                  : "bg-paper text-ink-soft border-border"
              }`}
              data-testid={`broadcast-channel-${c}`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={sending || !message.trim()}
            className="bg-terracotta hover:bg-terracotta/90 text-white"
            data-testid="broadcast-send-btn"
          >
            {sending ? "Sending…" : "Send broadcast"}
          </Button>
        </div>
      </div>
    </div>
  );
}
